import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { DAYS } from "../const/time";
import { DateHelper } from "@helpers/data-helper";
import { CNPJ } from "@helpers/cnpj";

export class CustomValidators {
  static periodGreaterThanFor(days: number, startDateFieldName: string, endDateFieldName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startDate = control.get(startDateFieldName);
      const endDate = control.get(endDateFieldName);

      if (startDate?.value !== null && endDate?.value !== null)
      {
        const startDateUnixUtc = (startDate.value as Date).getTime();
        const endDateUnixUtc = (endDate.value as Date).getTime();

        if ((endDateUnixUtc - startDateUnixUtc) / DAYS > days) {

          startDate.setErrors({ ...startDate.errors, periodGreaterThanDays: true });

          return { periodGreaterThanDays: true }
        } else {
          if (startDate.hasError('periodGreaterThanDays')) {
            startDate.setErrors(null);
          }
        }
      }

      return null;
    }
  }

  static MatchValidator(source: string, target: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const sourceCtrl = control.get(source);
      const targetCtrl = control.get(target);

      return sourceCtrl && targetCtrl && sourceCtrl.value !== targetCtrl.value
        ? { mismatch: true }
        : null;
    };
  }

  static notMatchValidator(source: string, target: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const sourceCtrl = control.get(source);
      const targetCtrl = control.get(target);

      return sourceCtrl && targetCtrl && sourceCtrl.value !== targetCtrl.value
        ? null
        : { notMismatch: true };
    };
  }

  static isValidCpf(checkString?: boolean): ValidatorFn {
    return (control: AbstractControl): Validators => {
      const cpf = control.value;

      if (cpf == null) return null;
      if (cpf == "") return null;
      if (cpf.length > 11) return null;
      if (cpf.length < 11) return null;

      if (checkString) {
        if (!/^[0-9]*$/.test(cpf)) {
          return { cpfNotValid: true };
        }
      } else {
        if (!/^[0-9]*$/.test(cpf)) {
          return null;
        }
      }

      if (cpf) {
        let numbers, digits, sum, i, result, equalDigits;
        equalDigits = 1;
        if (cpf.length < 11) {
          return null;
        }

        for (i = 0; i < cpf.length - 1; i++) {
          if (cpf.charAt(i) !== cpf.charAt(i + 1)) {
            equalDigits = 0;
            break;
          }
        }

        if (!equalDigits) {
          numbers = cpf.substring(0, 9);
          digits = cpf.substring(9);
          sum = 0;
          for (i = 10; i > 1; i--) {
            sum += numbers.charAt(10 - i) * i;
          }

          result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

          if (result !== Number(digits.charAt(0))) {
            return { cpfNotValid: true };
          }
          numbers = cpf.substring(0, 10);
          sum = 0;

          for (i = 11; i > 1; i--) {
            sum += numbers.charAt(11 - i) * i;
          }
          result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

          if (result !== Number(digits.charAt(1))) {
            return { cpfNotValid: true };
          }
          return null;
        } else {
          return { cpfNotValid: true };
        }
      }
      return null;
    };
  }

  static isValidCNPJ(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value = (control.value ?? '').toString().toUpperCase();
      if (!value) return null;

      const cnpj = value.replace(/[^0-9A-Z]/g, '');

      // enquanto digita, não acusa
      if (cnpj.length < 14) return null;

      // se passou de 14, é inválido
      if (cnpj.length > 14) return { cnpjNotValid: true };

      // 12 alfanum + 2 dígitos
      if (/[A-Z]/.test(cnpj)) {
        // se não respeita o padrão, não é CNPJ (provavelmente ID interno longo)
        if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj)) return null;

        return CNPJ.isValid(cnpj) ? null : { cnpjNotValid: true };
      }

      // -------- CNPJ NUMÉRICO (antigo) --------
      if (!/^\d{14}$/.test(cnpj)) return { cnpjNotValid: true };

      let tamanho = 12;
      let numeros = cnpj.substring(0, tamanho);
      let digitos = cnpj.substring(tamanho);
      let soma = 0;
      let pos = tamanho - 7;

      for (let i = tamanho; i >= 1; i--) {
        soma += Number(numeros.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
      }

      let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
      if (resultado !== Number(digitos.charAt(0))) return { cnpjNotValid: true };

      tamanho = 13;
      numeros = cnpj.substring(0, tamanho);
      soma = 0;
      pos = tamanho - 7;

      for (let i = tamanho; i >= 1; i--) {
        soma += Number(numeros.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
      }

      resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
      if (resultado !== Number(digitos.charAt(1))) return { cnpjNotValid: true };

      return null;
    };
  }

  static isDateGreaterThanToday(): ValidatorFn {
    return (control: AbstractControl): Validators => {
      let today: Date = DateHelper.currentDateIgnoreTime;

      if (control.value instanceof Date) {

      if (DateHelper.ignoreTime(control.value).getTime() > today.getTime()) {
        return { dateGreaterThanToday: true };
      }
    }

      return null;
    };
  }
  static isAgeAtLeast14Years(): ValidatorFn {
    return (control: AbstractControl): Validators => {
      let today: Date = new Date();
      let birthDate: Date = new Date(control.value);
      let ageDifference: number = today.getFullYear() - birthDate.getFullYear();
      let monthDifference: number = today.getMonth() - birthDate.getMonth();
      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        ageDifference--;
      }
      if (ageDifference >= 14)
        return null;

      return { ageLessThan14Years: true };
    };
  }

  static isDatePriorToToday(): ValidatorFn {
    return (control: AbstractControl): Validators => {
      let today: Date = new Date();
      today.setHours(0, 0, 0, 0);

      if (new Date(control.value) < today)
        return { datePriorToToday: true };

      return null;
    };
  }

  static isStartDateGreaterThanEndDate(
    start: string,
    end: string
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startControl = control.get(start);
      const endControl = control.get(end);

      if(startControl.value !== null && endControl.value !== null){
        if ((startControl.value as Date).getTime() > (endControl.value as Date).getTime()) {
          return { startGreaterThanEnd: true };
        }
      }

      return null;
    };
  }

  static isRequiredWhenFieldIsTrue(
    formGroup: FormGroup,
    fieldName: string
  ): ValidatorFn {
    return (control: AbstractControl) => {
      if (!formGroup) {
        return null;
      }

      return null;
    };
  }

  static isDateLowerThanLimit(minDate: Date) {
    return (control: AbstractControl) => {
      if (control.value < minDate) {
        return { dateLowerThanLimit: true };
      }

      return null;
    };
  }

  static isDateGreaterThanLimit(maxDate: Date) {
    return (control: AbstractControl) => {
      if (control.value > maxDate) {
        return { dateGreaterThanLimit: true };
      }

      return null;
    };
  }

  static periodGreaterThan365Days(
    startDateFieldName: string = "startDate",
    endDateFieldName: string = "endDate"
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let startDate = control.get(startDateFieldName).value as Date;
      let endDate = control.get(endDateFieldName).value as Date;

      if (!startDate || !endDate) {
        return null;
      }

      let daysPassed = (endDate.getTime() - startDate.getTime()) / DAYS;

      if (daysPassed > 365) {
        control.markAllAsTouched();

        control.get(startDateFieldName).setErrors({ ...control.get(startDateFieldName).errors, periodGreaterThan365Days: true })
        control.get(endDateFieldName).setErrors({ ...control.get(endDateFieldName).errors, periodGreaterThan365Days: true })

        return { periodGreaterThan365Days: true };
      }

      const startDateErrors = control.get(startDateFieldName).errors;
      const endDateErrors = control.get(endDateFieldName).errors;

      if (!startDateErrors && !endDateErrors) {
        return null;
      }

      if (startDateErrors && startDateErrors['periodGreaterThan365Days']) {
        delete startDateErrors['periodGreaterThan365Days'];

        control.get(startDateFieldName).markAsUntouched();
        control.markAsUntouched();
      }

      if (endDateErrors && endDateErrors['periodGreaterThan365Days']) {
        delete endDateErrors['periodGreaterThan365Days'];

        control.get(endDateFieldName).markAsUntouched();
        control.markAsUntouched();
      }

      if (startDateErrors && Object.keys(startDateErrors).length > 0) {
        control.get(startDateFieldName).setErrors(startDateErrors)
      } else {
        control.get(startDateFieldName).setErrors(null)
      }

      if (endDateErrors && Object.keys(endDateErrors).length > 0) {
        control.get(endDateFieldName).setErrors(endDateErrors)
      } else {
        control.get(endDateFieldName).setErrors(null)
      }

      return null;
    };
  }

  static periodGreaterThan(
    days: number,
    startDateFieldName: string = "startDate",
    endDateFieldName: string = "endDate"
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let startDate = control.get(startDateFieldName).value as Date;
      let endDate = control.get(endDateFieldName).value as Date;

      if (!startDate || !endDate) {
        return null;
      }

      let daysPassed = (DateHelper.ignoreTime(endDate).getTime() - DateHelper.ignoreTime(startDate).getTime()) / DAYS;
      if (daysPassed > days) {
        control.markAllAsTouched();

        control.get(startDateFieldName).setErrors({ ...control.get(startDateFieldName).errors, periodGreaterThan: true })
        control.get(endDateFieldName).setErrors({ ...control.get(endDateFieldName).errors, periodGreaterThan: true })


        return { periodGreaterThan: true };
      }



      const errors = control.get(startDateFieldName).errors;
      if (errors && errors['periodGreaterThan']) {
        delete errors['periodGreaterThan'];
      }

      if (errors && Object.keys(errors).length > 0) {
        control.get(startDateFieldName).setErrors(errors)
        control.get(endDateFieldName).setErrors(errors)
      } else {
        control.get(startDateFieldName).setErrors(null)
        control.get(endDateFieldName).setErrors(null)
      }


      return null;
    };
  }

  static shouldBeRequiredIfFieldIsNotNull(conditionField: string, fieldToRequire): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.get(conditionField).value && !control.get(fieldToRequire).value) {
        const error = {};

        error[fieldToRequire + "-required"] = true;
        return error;
      }

      return null;
    }
  }

  static yearBetween(startYear: number, endYear: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const year = control.value;

      if (year < startYear || year > endYear) {
        return { yearBetween: true };
      }

      return null;
    }

  }

  static noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isWhitespace = (control.value || '').trim().length === 0;
      const isValid = !isWhitespace;
      return isValid ? null : { 'whitespace': 'Campo vazio' };
    };
  }
}
